import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Truck, Edit2, Trash2, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { supplierService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const SUPPLIER_TYPES = [
  { value: 'raw_material', label: 'Raw Material' },
  { value: 'treatment', label: 'Treatment' },
  { value: 'machining', label: 'Machining' },
  { value: 'other', label: 'Other' },
];

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const { hasRole } = useAuth();

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    supplier_type: 'other',
    contact_person: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    loadSuppliers();
  }, [search, filterType]);

  const loadSuppliers = async () => {
    try {
      const params = { search, is_active: true };
      if (filterType) params.supplier_type = filterType;
      const response = await supplierService.getAll(params);
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        await supplierService.update(editingSupplier.id, formData);
        toast.success('Supplier updated successfully');
      } else {
        await supplierService.create(formData);
        toast.success('Supplier created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      code: supplier.code,
      name: supplier.name,
      supplier_type: supplier.supplier_type,
      contact_person: supplier.contact_person || '',
      email: supplier.email || '',
      phone: supplier.phone || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (supplier) => {
    if (window.confirm(`Are you sure you want to deactivate ${supplier.name}?`)) {
      try {
        await supplierService.delete(supplier.id);
        toast.success('Supplier deactivated');
        loadSuppliers();
      } catch (error) {
        toast.error('Failed to deactivate supplier');
      }
    }
  };

  const resetForm = () => {
    setEditingSupplier(null);
    setFormData({
      code: '',
      name: '',
      supplier_type: 'other',
      contact_person: '',
      email: '',
      phone: ''
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const getTypeLabel = (type) => {
    return SUPPLIER_TYPES.find(t => t.value === type)?.label || type;
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      raw_material: 'bg-blue-100 text-blue-700',
      treatment: 'bg-purple-100 text-purple-700',
      machining: 'bg-amber-100 text-amber-700',
      other: 'bg-slate-100 text-slate-700',
    };
    return colors[type] || colors.other;
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Suppliers</h1>
          <p className="text-sm text-slate-500 mt-1">Gestisci l'anagrafica fornitori</p>
        </div>
        {hasRole('admin', 'quality', 'production') && (
          <Button onClick={openNewDialog} data-testid="add-supplier-btn">
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi Fornitore
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per codice o nome..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-suppliers-input"
              />
            </div>
            <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-type-select">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {SUPPLIER_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : suppliers.length === 0 ? (
            <div className="text-center py-12">
              <Truck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No suppliers found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {suppliers.map((supplier) => (
                    <tr key={supplier.id} data-testid={`supplier-row-${supplier.id}`}>
                      <td className="font-mono text-sm font-medium">
                        <Link to={`/suppliers/${supplier.id}`} className="text-sky-600 hover:text-sky-800" data-testid={`open-supplier-${supplier.id}`}>
                          {supplier.code}
                        </Link>
                      </td>
                      <td className="font-medium">
                        <Link to={`/suppliers/${supplier.id}`} className="text-slate-900 hover:text-sky-700">
                          {supplier.name}
                        </Link>
                      </td>
                      <td>
                        <Badge className={getTypeBadgeColor(supplier.supplier_type)}>
                          {getTypeLabel(supplier.supplier_type)}
                        </Badge>
                      </td>
                      <td>{supplier.contact_person || '-'}</td>
                      <td className="text-sm text-slate-500">{supplier.email || '-'}</td>
                      <td className="text-sm text-slate-500">{supplier.phone || '-'}</td>
                      <td>
                        <Badge className={supplier.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                          {supplier.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <div className="flex items-center gap-1 justify-end">
                          <Link to={`/suppliers/${supplier.id}`}>
                            <Button variant="ghost" size="sm" title="Apri scheda">
                              <Award className="w-4 h-4 text-slate-500" />
                            </Button>
                          </Link>
                          {hasRole('admin', 'quality', 'production') && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(supplier)}>
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              {hasRole('admin') && (
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(supplier)}>
                                  <Trash2 className="w-4 h-4 text-rose-500" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSupplier ? 'Edit Supplier' : 'New Supplier'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                    disabled={!!editingSupplier}
                    data-testid="supplier-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="supplier_type">Type *</Label>
                  <Select
                    value={formData.supplier_type}
                    onValueChange={(value) => setFormData({ ...formData, supplier_type: value })}
                  >
                    <SelectTrigger data-testid="supplier-type-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPLIER_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Company Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="supplier-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_person">Contact Person</Label>
                <Input
                  id="contact_person"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-supplier-btn">
                {editingSupplier ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
